import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Leaf, Sprout, GlassWater, Wrench, Mail } from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { readApiError } from "@/lib/format";

// Phase 1.6 dark-editorial imagery
const HERO_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/3112c7fc-8e98-4a3a-b5ca-162acb16129b/images/f2f98769583fcbd9de9c7b1358e2f71677cd142f393667e5207358217c735ebc.png";
const BRAND_STRIP_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/3112c7fc-8e98-4a3a-b5ca-162acb16129b/images/b1531dfa478a7e2b687c31f873dc5fc91b5b56a4b54b126046166b0a8ec6d774.png";
const WORKSHOP_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/3112c7fc-8e98-4a3a-b5ca-162acb16129b/images/f5e421529ffdefe35a6c5e08fae3ba0bab5606d51c8e723682fb21e26625caf9.png";
const SUNDAY_NOTES_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/3112c7fc-8e98-4a3a-b5ca-162acb16129b/images/96b8239fbd7548f0db1adab1fce17ab2e1dd2532741791714d460fbe42beaee6.png";

const CATEGORY_CARDS = [
  {
    slug: "bonsai",
    label: "Bonsai",
    icon: Leaf,
    image:
      "https://static.prod-images.emergentagent.com/jobs/3112c7fc-8e98-4a3a-b5ca-162acb16129b/images/00d67fa453e54f87dc8e7ba6a506c118fd4f879f422c6afacb81c5ad090e443d.png",
    blurb: "Hand-trained trees on the workbench for years.",
  },
  {
    slug: "succulents",
    label: "Succulents",
    icon: Sprout,
    image:
      "https://static.prod-images.emergentagent.com/jobs/3112c7fc-8e98-4a3a-b5ca-162acb16129b/images/568cbe699f9af336ffafe55a4e5518bcfaefc8af86651c848e1931c83ceb55a5.png",
    blurb: "Almost impossible to kill. Easy to fall in love with.",
  },
  {
    slug: "terrariums",
    label: "Terrariums",
    icon: GlassWater,
    image:
      "https://static.prod-images.emergentagent.com/jobs/3112c7fc-8e98-4a3a-b5ca-162acb16129b/images/613610d7b76282e2b9b10648398814787ab2ada89968bf6ac1bb9ecea862a116.png",
    blurb: "Small glass gardens shaped around driftwood and moss.",
  },
  {
    slug: "accessories",
    label: "Accessories",
    icon: Wrench,
    image:
      "https://static.prod-images.emergentagent.com/jobs/3112c7fc-8e98-4a3a-b5ca-162acb16129b/images/8e64cdbaf5016fb439bca7cd264c4c6da34109affb108422467c440fe210023e.png",
    blurb: "Honest tools. Brass, ceramic, coastal stones.",
  },
];

function CommunityPitch() {
  const [loading, setLoading] = useState(false);
  const join = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/checkout/membership`, {
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error("Could not start checkout", { description: readApiError(e) });
    } finally {
      setLoading(false);
    }
  };
  return (
    <section
      data-testid="home-community"
      className="border-y border-[#5E594E]/60 bg-[#4D4940]"
    >
      <div className="mx-auto grid max-w-6xl gap-12 px-6 py-24 md:grid-cols-[1.2fr_1fr]">
        <div className="space-y-6">
          <p className="text-xs uppercase tracking-[0.24em] text-[#C9A961]">
            The Coastal Roots Community
          </p>
          <h2 className="font-serif text-4xl tracking-wide text-[#F5EFE2] sm:text-5xl">
            A quieter place to grow, together.
          </h2>
          <p className="max-w-xl text-base leading-relaxed text-[#F5EFE2]/85">
            Fifty dollars a month gets you a seat at the workbench. Live
            monthly workshops with me, a member-only cuttings exchange, and
            the full library of pruning, repotting, and terrarium playbooks.
          </p>
          <Button
            data-testid="home-community-cta"
            onClick={join}
            disabled={loading}
            size="lg"
            className="bg-[#C9A961] text-[#3D3A33] hover:bg-[#DDBE7A]"
          >
            {loading ? "Opening Stripe…" : "Join for $50/month"}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
        <ul className="space-y-5 text-[#F5EFE2]/90">
          {[
            "Live monthly workshops with Kenny — wiring, pruning, repotting on camera, your questions in chat.",
            "Member-only cuttings & seed exchange — share what you're growing, get something back from the coast.",
            "The full pruning library — every technique I've worked out over the last decade, written down.",
          ].map((b) => (
            <li key={b} className="flex gap-3">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#C9A961]" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function EmailCapture() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Drop your email in first");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post(`/leads/subscribe`, {
        email: email.trim(),
        source: "homepage_footer",
      });
      if (res.data?.duplicate) {
        toast("You're already on the list", {
          description: "We'll be in touch Sunday.",
        });
      } else {
        toast.success("On the list", {
          description: "Look out for Kenny's note on Sunday morning.",
        });
      }
      setEmail("");
    } catch (e) {
      toast.error("Could not subscribe", { description: readApiError(e) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="home-leads" className="bg-[#3D3A33]">
      {/* Sunday Notes promo block — image + editorial copy */}
      <div className="mx-auto max-w-6xl px-6 pb-12 pt-24">
        <div
          data-testid="home-sunday-promo"
          className="grid gap-10 overflow-hidden rounded-2xl border border-[#5E594E] bg-[#4D4940] md:grid-cols-[1.05fr_1fr]"
        >
          <div className="relative aspect-[4/3] md:aspect-auto">
            <img
              src={SUNDAY_NOTES_IMAGE}
              alt="A quiet Sunday morning at the workbench"
              className="h-full w-full object-cover"
              loading="lazy"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#4D4940]/60" />
          </div>
          <div className="flex flex-col justify-center gap-5 p-8 md:p-10">
            <span className="inline-flex w-fit items-center gap-2 rounded-full border border-[#C9A961]/40 bg-[#C9A961]/10 px-3 py-1 text-xs uppercase tracking-[0.22em] text-[#C9A961]">
              <Mail className="h-3 w-3" />
              Sunday Notes
            </span>
            <p className="font-serif text-3xl leading-snug text-[#F5EFE2] sm:text-4xl">
              One well-written note from Kenny each Sunday morning.
            </p>
            <p className="text-base leading-relaxed text-[#C9BEA8]">
              No sales. No spam. Just whatever I learned that week from the
              trees.
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-6 pb-24 text-center">
        <form
          onSubmit={submit}
          className="mx-auto flex max-w-md flex-col gap-3 sm:flex-row"
        >
          <Input
            data-testid="leads-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="border-[#5E594E] bg-[#4D4940] text-[#F5EFE2]"
          />
          <Button
            data-testid="leads-subscribe-btn"
            type="submit"
            disabled={loading}
            className="bg-[#C9A961] text-[#3D3A33] hover:bg-[#DDBE7A]"
          >
            {loading ? "…" : "Subscribe"}
          </Button>
        </form>
        <p className="mt-3 text-xs text-[#7A8B6F]">
          Unsubscribe in one click. We never sell our list.
        </p>
      </div>
    </section>
  );
}

function WorkshopStrip() {
  return (
    <section
      data-testid="home-workshop-strip"
      className="relative isolate overflow-hidden bg-[#3D3A33]"
    >
      <div className="absolute inset-0 -z-10">
        <img
          src={WORKSHOP_IMAGE}
          alt="The workshop interior, late afternoon light"
          className="h-full w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#3D3A33] via-[#3D3A33]/85 to-[#3D3A33]/30" />
      </div>
      <div className="mx-auto max-w-6xl px-6 py-24 sm:py-32">
        <div className="max-w-xl space-y-5">
          <p className="text-xs uppercase tracking-[0.28em] text-[#C9A961]">
            The Workshop
          </p>
          <p className="font-serif text-3xl leading-snug tracking-wide text-[#F5EFE2] sm:text-4xl">
            Every tree leaves the workshop with a name, a story, and a card
            written by Kenny.
          </p>
          <p className="text-base leading-relaxed text-[#C9BEA8]">
            Nothing in the shop is mass-shipped from a warehouse. Each piece
            spends weeks — sometimes years — on the bench before it's ready
            to leave the coast.
          </p>
          <Link
            to="/about"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#C9A961] hover:text-[#DDBE7A] hover:underline underline-offset-4"
          >
            Step into the studio
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [membershipLoading, setMembershipLoading] = useState(false);

  const startMembership = async () => {
    setMembershipLoading(true);
    try {
      const res = await api.post(`/checkout/membership`, {
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error("Could not start checkout", { description: readApiError(e) });
    } finally {
      setMembershipLoading(false);
    }
  };

  return (
    <PublicLayout overlayNav padTop={false}>
      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative isolate flex min-h-[92vh] items-center overflow-hidden"
      >
        <div className="absolute inset-0 z-0">
          <img
            src={HERO_IMAGE}
            alt="Twilight on the workbench — a bonsai under a single lamp"
            className="h-full w-full object-cover object-center"
            style={{ filter: "brightness(1.45) contrast(1.06) saturate(1.08)" }}
          />
          {/* Soft left wash so the headline + body read cleanly */}
          <div className="absolute inset-y-0 left-0 w-3/5 bg-gradient-to-r from-[#3D3A33]/80 via-[#3D3A33]/45 to-transparent" />
          {/* Subtle bottom fade into next section */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-[#3D3A33]" />
        </div>
        <div className="relative z-10 mx-auto w-full max-w-6xl px-6 pt-32 pb-20">
          <div className="max-w-2xl space-y-6 text-[#F5EFE2]">
            <p className="text-xs uppercase tracking-[0.32em] text-[#C9A961]">
              Pacific Coast · Hand-shaped
            </p>
            <h1
              data-testid="hero-headline"
              className="font-serif text-5xl leading-[1.02] tracking-wide sm:text-6xl lg:text-7xl"
            >
              Grow Something Real.
            </h1>
            <p className="max-w-xl text-base leading-relaxed text-[#F5EFE2]/90 sm:text-lg">
              Bonsai, succulents, and custom terrariums shaped by hand on the
              Pacific coast. Join the Coastal Roots community for $50/month —
              learn the craft, share the cuttings, and put down roots that last.
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Button
                data-testid="hero-cta-join"
                onClick={startMembership}
                disabled={membershipLoading}
                size="lg"
                className="bg-[#C9A961] text-[#3D3A33] hover:bg-[#DDBE7A]"
              >
                {membershipLoading ? "Opening Stripe…" : "Join the Community — $50/mo"}
                <ArrowRight className="h-4 w-4" />
              </Button>
              <Link to="/shop">
                <Button
                  data-testid="hero-cta-shop"
                  size="lg"
                  variant="outline"
                  className="border-[#C9A961]/70 bg-transparent text-[#C9A961] hover:bg-[#C9A961] hover:text-[#3D3A33]"
                >
                  Shop the Collection
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* BRAND STRIP */}
      <section data-testid="home-brand-strip" className="bg-[#3D3A33]">
        <div className="mx-auto grid max-w-6xl gap-12 px-6 py-24 md:grid-cols-2 md:items-center">
          <div className="overflow-hidden rounded-2xl border border-[#5E594E]">
            <img
              src={BRAND_STRIP_IMAGE}
              alt="Kenny's hands wiring a juniper branch"
              className="aspect-[4/5] w-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="space-y-5">
            <p className="text-xs uppercase tracking-[0.24em] text-[#C9A961]">
              From Kenny
            </p>
            <h2 className="font-serif text-4xl tracking-wide text-[#F5EFE2] sm:text-5xl">
              Three years to grow.
              <br />
              Thirty seconds to root.
            </h2>
            <p className="text-base leading-relaxed text-[#F5EFE2]/85">
              I&apos;m Kenny. I started Coastal Roots Botanica out of a salt-warped
              workshop on the Pacific coast because the world doesn&apos;t need
              another rushed thing. It needs slower things — a juniper bonsai
              that took three years to bend the way you wanted, a terrarium
              that lives on your kitchen counter for a decade, a succulent that
              came from a cutting your friend gave you in 2019. The community
              we&apos;re building is for people who want to grow that kind of thing
              — together.
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 text-sm font-medium text-[#C9A961] underline-offset-4 hover:text-[#DDBE7A] hover:underline"
              data-testid="brand-strip-about-link"
            >
              Read the long version
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CATEGORY CARDS */}
      <section data-testid="home-categories" className="bg-[#3D3A33]">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="mb-10 max-w-xl space-y-3">
            <p className="text-xs uppercase tracking-[0.24em] text-[#C9A961]">
              The Collection
            </p>
            <h2 className="font-serif text-4xl tracking-wide text-[#F5EFE2]">
              Four quiet ways in.
            </h2>
          </div>
          <div className="grid gap-6 sm:grid-cols-2">
            {CATEGORY_CARDS.map((c) => {
              const Icon = c.icon;
              return (
                <Link
                  key={c.slug}
                  to={`/shop/${c.slug}`}
                  data-testid={`category-card-${c.slug}`}
                  className="group relative overflow-hidden rounded-2xl border border-[#5E594E] bg-[#4D4940] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#C9A961]/60 hover:shadow-[0_8px_30px_rgba(201,169,97,0.18)]"
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={c.image}
                      alt={c.label}
                      className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                    />
                    {/* Strong dark gradient over bottom 40% so category name is legible */}
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#3D3A33] via-[#3D3A33]/70 to-transparent" />
                  </div>
                  <div className="space-y-2 p-6">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#C9A961]">
                      <Icon className="h-3.5 w-3.5" />
                      Coastal Roots
                    </div>
                    <h3 className="font-serif text-2xl tracking-wide text-[#F5EFE2]">
                      <span className="relative inline-block">
                        {c.label}
                        <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#C9A961] transition-all duration-500 group-hover:w-full" />
                      </span>
                    </h3>
                    <p className="text-sm leading-relaxed text-[#C9BEA8]">
                      {c.blurb}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* THE WORKSHOP — new in Phase 1.6 */}
      <WorkshopStrip />

      <CommunityPitch />
      <EmailCapture />
    </PublicLayout>
  );
}
